import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { SectionCard, formatCurrency, percentChange } from '../../../components/design-system/admin';
import type { OverviewResponse } from '../../../common/models/admin-overview.model';

/** What the models cost this month (from ai_requests), and which features spend it. */
export function AiSpendCard({ ai, className }: { ai: OverviewResponse['health']['ai']; className?: string }) {
  const { t } = useTranslation();
  const top = Math.max(...ai.byFeature.map((f) => f.cost), 0.0001);
  const change = percentChange(ai.costMonth, ai.costPreviousMonth);

  return (
    <SectionCard
      title={t('ADMIN_OVERVIEW.AI.TITLE')}
      description={t('ADMIN_OVERVIEW.AI.DESCRIPTION')}
      className={className}
      testId="overview-ai"
      action={
        <Link to="/costs" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="overview-ai-costs">
          {t('ADMIN_COSTS.OPEN_PAGE')}
          <ArrowRight className="size-3" />
        </Link>
      }
    >
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-3xl leading-none font-medium tracking-tight tabular-nums">{formatCurrency(ai.costMonth, 'usd')}</span>
        {change !== null && <span className="text-xs text-muted-foreground tabular-nums">{change > 0 ? '+' : ''}{Math.round(change)}%</span>}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
        {t('ADMIN_OVERVIEW.AI.REQUESTS', { count: ai.requestsMonth })}
        {' · '}
        <span className={ai.errorsMonth ? 'text-destructive' : undefined}>{t('ADMIN_OVERVIEW.AI.ERRORS', { count: ai.errorsMonth })}</span>
        {' · '}
        {t('ADMIN_OVERVIEW.AI.LAST_MONTH', { amount: formatCurrency(ai.costPreviousMonth, 'usd') })}
      </p>

      {ai.byFeature.length ? (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-muted-foreground">{t('ADMIN_OVERVIEW.AI.BY_FEATURE')}</p>
          <ul className="flex flex-col gap-2">
            {ai.byFeature.map((f) => (
              <li key={f.feature} className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-mono">{f.feature || '—'}</span>
                  <span className="shrink-0 text-muted-foreground tabular-nums">{formatCurrency(f.cost, 'usd')}</span>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-foreground/70" style={{ width: `${(f.cost / top) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">{t('ADMIN_OVERVIEW.AI.NONE')}</p>
      )}
    </SectionCard>
  );
}
